import { TagIcon, UserCircleIcon, UserIcon } from '@heroicons/react/24/outline';
import {
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter,
  type ActiveFilter,
  useFiltersFromLocalStorage
} from '@oysterjs/ui/search-filter';
import {
  OutboundCallStatus,
  type AgencyUserFieldsFragment,
  type OutboundCallsFilterInput,
  type VoiceAgentOverviewFieldsFragment
} from '~/api/graphql';
import { useAgencyUsers } from '~/components/current-user';
import React from 'react';

export const OUTBOUND_CALLS_FILTER_STORAGE_KEY = 'cara_outbound_calls_default_view';

export function useOutboundCallFilters(voiceAgents: VoiceAgentOverviewFieldsFragment[]) {
  const agencyUsers = useAgencyUsers();
  const filterDefinitions = React.useMemo(
    () => createOutboundCallFilterDefinitions(agencyUsers, voiceAgents),
    [agencyUsers, voiceAgents]
  );
  const { activeFilters, setActiveFilters, initialFilters } =
    useFiltersFromLocalStorage<OutboundCallFilter>(
      OUTBOUND_CALLS_FILTER_STORAGE_KEY,
      filterDefinitions
    );
  const currentFilter = React.useMemo(
    () => activeFiltersToOutboundCallFilter(activeFilters),
    [activeFilters]
  );
  return { filterDefinitions, activeFilters, setActiveFilters, initialFilters, currentFilter };
}

export type OutboundCallFilter = 'status' | 'voiceAgent' | 'createdBy' | 'myCalls';

const STATUS_LABELS: Record<OutboundCallStatus, string> = {
  [OutboundCallStatus.PendingConfirmation]: 'Pending confirmation',
  [OutboundCallStatus.Scheduled]: 'Scheduled',
  [OutboundCallStatus.Dialing]: 'Dialing',
  [OutboundCallStatus.InProgress]: 'In progress',
  [OutboundCallStatus.Completed]: 'Completed',
  [OutboundCallStatus.Failed]: 'Failed',
  [OutboundCallStatus.Cancelled]: 'Cancelled',
  [OutboundCallStatus.NoAnswer]: 'No answer',
  [OutboundCallStatus.VoicemailLeft]: 'Voicemail left'
};

export function activeFiltersToOutboundCallFilter(
  filters: ActiveFilter<OutboundCallFilter>[]
): OutboundCallsFilterInput | undefined {
  if (filters.length === 0) return undefined;
  const out: OutboundCallsFilterInput = {};
  for (const f of filters) {
    switch (f.type) {
      case 'status':
        out.status = f.value as OutboundCallStatus;
        break;
      case 'voiceAgent':
        out.voiceAgentId = f.value as string;
        break;
      case 'createdBy':
        out.createdByUserId = f.value as string;
        break;
      case 'myCalls':
        out.createdByMe = true;
        break;
      default:
        break;
    }
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

export class OutboundCallStatusFilter implements FilterDefinition<OutboundCallFilter> {
  readonly type = 'status' as const;
  readonly icon = TagIcon;
  readonly priority = 0;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<OutboundCallFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    return Object.values(OutboundCallStatus)
      .filter((status) => STATUS_LABELS[status].toLowerCase().includes(q) || 'status'.includes(q))
      .map((status) => ({
        value: status,
        displayLabel: 'Status is',
        displayValue: STATUS_LABELS[status]
      }));
  }

  deserialize(s: SerializedFilter<OutboundCallFilter>): ActiveFilter<OutboundCallFilter> | null {
    if (!Object.values(OutboundCallStatus).includes(s.value as OutboundCallStatus)) {
      return null;
    }
    return {
      id: `status-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Status is',
      displayValue: s.displayValue
    };
  }
}

export class OutboundCallMyCallsFilter implements FilterDefinition<OutboundCallFilter> {
  readonly type = 'myCalls' as const;
  readonly icon = UserIcon;
  readonly priority = 1;
  readonly isUnique = true;

  generate({ query }: FilterGeneratorContext<OutboundCallFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    if (
      q.length > 0 &&
      !['my', 'mine', 'me', 'my calls', 'created by me'].some(
        (term) => term.includes(q) || q.includes(term)
      )
    ) {
      return [];
    }
    return [
      {
        value: 'true',
        displayLabel: 'Created by me',
        displayValue: 'Yes'
      }
    ];
  }

  deserialize(s: SerializedFilter<OutboundCallFilter>): ActiveFilter<OutboundCallFilter> {
    return {
      id: 'my-calls',
      type: this.type,
      value: s.value,
      displayLabel: 'Created by me',
      displayValue: s.displayValue
    };
  }
}

export class OutboundCallCreatorFilter implements FilterDefinition<OutboundCallFilter> {
  readonly type = 'createdBy' as const;
  readonly icon = UserIcon;
  readonly priority = 8;
  readonly isUnique = true;

  constructor(private readonly agencyUsers: AgencyUserFieldsFragment[]) {}

  generate({ query }: FilterGeneratorContext<OutboundCallFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    const candidates =
      q.length === 0
        ? this.agencyUsers.slice(0, 20)
        : this.agencyUsers.filter(
            (user) =>
              user.name.toLowerCase().includes(q) ||
              user.email.toLowerCase().includes(q) ||
              'created by'.includes(q)
          );

    return candidates.slice(0, 20).map((user) => ({
      value: user.id,
      displayLabel: 'Created by',
      displayValue: user.name || user.email
    }));
  }

  deserialize(s: SerializedFilter<OutboundCallFilter>): ActiveFilter<OutboundCallFilter> {
    return {
      id: `createdBy-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Created by',
      displayValue: s.displayValue
    };
  }
}

export class OutboundVoiceAgentFilter implements FilterDefinition<OutboundCallFilter> {
  readonly type = 'voiceAgent' as const;
  readonly icon = UserCircleIcon;
  readonly priority = 5;
  readonly isUnique = true;

  constructor(private readonly voiceAgents: VoiceAgentOverviewFieldsFragment[]) {}

  generate({ query }: FilterGeneratorContext<OutboundCallFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    return this.voiceAgents
      .filter(
        (agent) =>
          agent.name.toLowerCase().includes(q) || 'agent'.includes(q) || 'voice'.includes(q)
      )
      .map((agent) => ({
        value: agent.id,
        displayLabel: 'Voice agent is',
        displayValue: agent.name
      }));
  }

  deserialize(s: SerializedFilter<OutboundCallFilter>): ActiveFilter<OutboundCallFilter> {
    return {
      id: `voiceAgent-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Voice agent is',
      displayValue: s.displayValue
    };
  }
}

export const createOutboundCallFilterDefinitions = (
  agencyUsers: AgencyUserFieldsFragment[],
  voiceAgents: VoiceAgentOverviewFieldsFragment[]
): FilterDefinition<OutboundCallFilter>[] => [
  new OutboundCallMyCallsFilter(),
  new OutboundCallStatusFilter(),
  new OutboundCallCreatorFilter(agencyUsers),
  new OutboundVoiceAgentFilter(voiceAgents)
];
